import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Network,
  KeyRound,
  Shield,
} from "lucide-react";
import { getTenants } from "@/api/endpoints/tenants";
import {
  reprovisionTenant,
  deleteTenant,
  type ReprovisionReport,
  type ProvisionStatus,
} from "@/api/endpoints/tenants";
import { Trash2 } from "lucide-react";
import PageHelp from "@/components/Helper/PageHelp";
import { openHelper } from "@/components/Helper/HelperWidget";

// Admin tenants page — single source of truth for the
// NetBox/Keycloak/Vault mapping status of every tenant. The
// "Re-provision" button hits the idempotent backend endpoint that
// re-runs the auto-provision logic for any service still missing.

function statusBadge(s: ProvisionStatus | undefined): {
  text: string;
  cls: string;
} {
  if (!s) return { text: "—", cls: "bg-gray-800 text-gray-500" };
  if (s.status === "failed")
    return { text: `failed: ${s.error?.slice(0, 60)}`, cls: "bg-red-900/40 text-red-300" };
  if (s.status === "already_mapped" || s.status === "ensured")
    return { text: s.status, cls: "bg-emerald-900/40 text-emerald-300" };
  if (s.status === "provisioned")
    return { text: "just provisioned", cls: "bg-blue-900/40 text-blue-300" };
  return { text: s.status, cls: "bg-gray-800 text-gray-300" };
}

export default function AdminTenants() {
  const queryClient = useQueryClient();
  const tenantsQuery = useQuery({ queryKey: ["tenants"], queryFn: getTenants });
  const [reports, setReports] = useState<Record<number, ReprovisionReport>>({});

  const reprovMut = useMutation({
    mutationFn: (id: number) => reprovisionTenant(id),
    onSuccess: (data) => {
      setReports((prev) => ({ ...prev, [data.tenant_id]: data }));
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: ({ id, force }: { id: number; force: boolean }) =>
      deleteTenant(id, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
  });

  const handleDelete = (id: number, slug: string) => {
    // Confirm once; if backend refuses with 409 (VMs present), ask for
    // a second explicit confirm before passing force=true so a misclick
    // can't wipe production VMs.
    if (!window.confirm(`Supprimer le tenant "${slug}" ? Keycloak + NetBox seront nettoyes.`)) {
      return;
    }
    deleteMut.mutate(
      { id, force: false },
      {
        onError: (err) => {
          const msg = (err as Error).message;
          if (msg.includes("409") && window.confirm(
            `Ce tenant a des VMs attachees. Confirmer la suppression CASCADE (les VMs seront aussi detruites) ?`,
          )) {
            deleteMut.mutate({ id, force: true });
          }
        },
      },
    );
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Building2 className="h-7 w-7 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Tenants</h1>
            <p className="text-sm text-gray-400">
              Etat NetBox / Keycloak / Vault par tenant. Re-provisionne les
              mappings manquants en un clic (idempotent).
            </p>
          </div>
        </div>
        <PageHelp onAskHelper={openHelper} />
      </div>

      {tenantsQuery.isLoading && (
        <p className="text-sm text-gray-500">Chargement...</p>
      )}
      {tenantsQuery.error && (
        <p className="flex items-center gap-2 text-sm text-red-400">
          <AlertTriangle className="h-4 w-4" />{" "}
          {(tenantsQuery.error as Error).message}
        </p>
      )}

      {tenantsQuery.data && (
        <div className="overflow-x-auto rounded-lg border border-gray-800 bg-gray-900">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 font-medium">Tenant</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">
                  <Network className="mr-1 inline h-3.5 w-3.5" /> NetBox
                </th>
                <th className="px-4 py-3 font-medium">
                  <KeyRound className="mr-1 inline h-3.5 w-3.5" /> Keycloak
                </th>
                <th className="px-4 py-3 font-medium">
                  <Shield className="mr-1 inline h-3.5 w-3.5" /> Vault
                </th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {tenantsQuery.data.map((t) => {
                const tid = Number(t.id);
                const rep = reports[tid];
                return (
                  <tr key={t.id} className="hover:bg-gray-900/30">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-200">{t.name}</div>
                      <div className="font-mono text-xs text-gray-500">
                        #{t.id} {t.slug}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      {t.role ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      {t.netbox_tenant_id != null ? (
                        <span className="rounded bg-emerald-900/40 px-2 py-0.5 text-xs text-emerald-300">
                          mapped #{t.netbox_tenant_id}
                        </span>
                      ) : (
                        <span className="rounded bg-amber-900/40 px-2 py-0.5 text-xs text-amber-300">
                          missing
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {t.keycloak_group_id ? (
                        <span className="rounded bg-emerald-900/40 px-2 py-0.5 font-mono text-xs text-emerald-300">
                          {t.keycloak_group_id.slice(0, 8)}...
                        </span>
                      ) : (
                        <span className="rounded bg-amber-900/40 px-2 py-0.5 text-xs text-amber-300">
                          missing
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-400">
                        tenants/{t.slug}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => reprovMut.mutate(tid)}
                          disabled={
                            reprovMut.isPending && reprovMut.variables === tid
                          }
                          className="inline-flex items-center gap-1.5 rounded border border-gray-700 bg-gray-800 px-2.5 py-1 text-xs text-gray-200 hover:bg-gray-700 disabled:opacity-50"
                        >
                          {reprovMut.isPending && reprovMut.variables === tid ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3 w-3" />
                          )}
                          Re-provision
                        </button>
                        <button
                          onClick={() => handleDelete(tid, t.slug)}
                          disabled={
                            deleteMut.isPending &&
                            deleteMut.variables?.id === tid
                          }
                          title="Supprimer le tenant (refuse si VMs attachees)"
                          className="inline-flex items-center gap-1.5 rounded border border-red-900/50 bg-red-900/20 px-2.5 py-1 text-xs text-red-300 hover:bg-red-900/40 disabled:opacity-50"
                        >
                          {deleteMut.isPending &&
                          deleteMut.variables?.id === tid ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </button>
                      </div>
                      {rep && (
                        <div className="mt-2 space-y-0.5 text-right text-[10px]">
                          {(["netbox", "keycloak", "vault"] as const).map(
                            (k) => {
                              const b = statusBadge(rep[k]);
                              return (
                                <div key={k}>
                                  <span className="text-gray-500">
                                    {k}:{" "}
                                  </span>
                                  <span
                                    className={`inline-block rounded px-1 ${b.cls}`}
                                  >
                                    {b.text}
                                  </span>
                                </div>
                              );
                            },
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {reprovMut.isError && (
        <div className="flex items-center gap-2 rounded border border-red-700/40 bg-red-900/20 p-3 text-sm text-red-300">
          <AlertTriangle className="h-4 w-4" />{" "}
          {(reprovMut.error as Error).message}
        </div>
      )}
      {reprovMut.isSuccess && (
        <div className="flex items-center gap-2 rounded border border-emerald-700/40 bg-emerald-900/20 p-3 text-sm text-emerald-300">
          <CheckCircle2 className="h-4 w-4" /> Re-provision termine pour tenant
          #{reprovMut.data?.tenant_id}.
        </div>
      )}
    </div>
  );
}
